import { useMemo, MutableRefObject } from 'react'
import { FormattedMessage } from 'react-intl'
import { deburr } from 'lodash'
import { Box, ListItemIcon, ListItemText, TextFieldProps } from '@mui/material'
import { CheckOutlined } from '@mui/icons-material'
import { Typography } from '@carto/meridian-ds/components'

import EllipsisWithTooltip from 'features/common/ui/utils/EllipsisWithTooltip'
import TextFieldWithSuggestions, { defaultFilterSuggestions } from 'features/common/ui/TextFieldWithSuggestions'
import MessageField from 'features/common/ui/Forms/MessageField'
import { User, UserRoleLabels } from 'features/users/types'
import { getRole } from 'features/users/model/user'
import UserAvatar from 'features/users/ui/UserAvatar'

import { CommentContentCommon, CommentUser } from 'features/collaboration/types'
import { getMentions } from 'features/collaboration/utils/mentions'

const MAX_COMMENT_LENGTH = 2101
export type CommentTextFieldValue = Pick<CommentContentCommon, 'content' | 'mentions'>

export type CommentTextFieldProps = {
  value: CommentTextFieldValue
  onChange: (value: CommentTextFieldValue) => void
  users: User[]
  placeholder?: string
  isEditing?: boolean

  onSubmit: () => void
  textInputRef?: MutableRefObject<HTMLInputElement | null>
} & Omit<TextFieldProps, 'value' | 'onChange'>

type UserSuggestion = {
  value: string
  user: User
}

const ownerLabel = 'Owner'
function UserSuggestion({ user }: { user: CommentUser }) {
  const roleLabel = user.isOwner ? ownerLabel : UserRoleLabels[getRole(user)]
  return (
    <>
      <ListItemIcon>
        <UserAvatar user={user} />
      </ListItemIcon>
      <ListItemText>
        <Box display="flex" flexDirection="column">
          <Typography variant="body2">
            <EllipsisWithTooltip>{user.email}</EllipsisWithTooltip>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {roleLabel}
          </Typography>
        </Box>
      </ListItemText>
    </>
  )
}

const tooManySuggestionsMessage = <FormattedMessage id="collaboration.comments.maxSuggestionExceededMessage" />
const noSuggestionsMessage = <FormattedMessage id="collaboration.comments.noSuggestionsMessage" />

export default function CommentTextField({
  value,
  onChange,
  onSubmit,
  users,
  placeholder,
  textInputRef,
  isEditing,
  ...textFieldProps
}: CommentTextFieldProps) {
  const suggestions: UserSuggestion[] = useMemo(
    () => users.map((user) => ({ value: `@${deburr(user.email).toLowerCase()}`, user })),
    [users]
  )

  const getSuggestions = (currentWord?: string) => {
    const cleared = deburr(currentWord).substring(1).toLowerCase()
    return defaultFilterSuggestions(suggestions, cleared)
  }

  const renderSuggestion = (suggestion: UserSuggestion) => <UserSuggestion user={suggestion.user} />

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value
    if (newValue.length > MAX_COMMENT_LENGTH) {
      newValue = newValue.slice(0, MAX_COMMENT_LENGTH)
    }
    onChange({ ...value, content: newValue, mentions: getMentions(newValue, users) })
  }

  return (
    <TextFieldWithSuggestions
      textInputRef={textInputRef}
      value={value.content}
      onChange={handleChange}
      suggestionRegexp={/@/}
      getSuggestions={getSuggestions}
      renderSuggestion={renderSuggestion}
      maxSuggestionExceededMessage={tooManySuggestionsMessage}
      noSuggestionsMessage={noSuggestionsMessage}
      renderTextField={(props) => (
        <MessageField
          value={value.content}
          onClick={onSubmit}
          placeholder={placeholder}
          textFieldProps={props}
          iconButton={isEditing ? <CheckOutlined /> : undefined}
        />
      )}
      {...textFieldProps}
    />
  )
}
